import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Container,
    SimpleTextField,
    SimpleButton,
    SimpleSingleSelect,
    Loading,
    ErrorMessage,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import { getZeroOption, type Option } from '@/src/utils';
import type { IdResponseDTO, ExamsResponseDTO, CreateExamAssignmentDTO, ExamOption } from './DTOs';

const LoadingOption = getZeroOption('Loading assessments...');
const SelectExamOption = getZeroOption('Select an assessment');

const CreateExamAttemptPage: React.FC = () => {
    document.title = 'Create Exam Attempt';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [attemptId, setAttemptId] = useState(0);
    const [examData, setExamData] = useState<ExamOption[]>([]);
    const [examOptions, setExamOptions] = useState<Option[]>([LoadingOption]);
    const [createdAttemptId, setCreatedAttemptId] = useState(0);

    const [examSearch, setExamSearch] = useState('');
    const [selectedExamId, setSelectedExamId] = useState<number>(0);

    const navigate = useNavigate();

    const handleSetAttemptId = () => {
        navigate(`/studentExamView/attempt/${attemptId}/start`);
    };

    useEffect(() => {
        (async function init() {
            try {
                setError('');
                const res = await apiRequest<ExamsResponseDTO>(
                    'get',
                    'utility/filterData?getExam=true&showFullExamList=true&showPublishedExamsOnly=true'
                );
                const data = res.data.examOptions;
                setExamData(data);
                setExamOptions([
                    SelectExamOption,
                    ...data.map((opt) => ({ id: opt.id, name: opt.name })),
                ]);
            } catch {
                setError('Failed to load exam options.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filteredExamOptions = examOptions.filter((opt) =>
        opt.name.toLowerCase().includes(examSearch.toLowerCase())
    );

    const handleCreateAttempt = useCallback(async () => {
        try {
            if (loading) {
                return;
            }
            if (createdAttemptId > 0) {
                setError(
                    'You have already created an attempt. Please use the Load Attempt button to begin.'
                );
                return;
            }
            setError('');

            const selectedExam = examData.find((opt) => opt.id === selectedExamId);
            const today = new Date();
            const inTwoWeeks = new Date(today);
            inTwoWeeks.setDate(inTwoWeeks.getDate() + 14);

            const classId = 1;
            const studentId = 37;
            const teacherId = 157;

            const createAssignmentParams = {
                examId: selectedExamId,
                schoolId: 3,
                description: 'Auto generated admin attempt',
                numberOfAttemptsAllowed: 1,
                duration: 0,
                fromDTUTC: today.toISOString(),
                toDTUTC: inTwoWeeks.toISOString(),
                examAssignmentTypeMasterId: 1,
                gradeLevelMasterId: 1,
                examTypeMasterId: selectedExam!.examTypeMasterId,
                isSpanishAllowed: true,
                classes: [
                    {
                        classId: classId,
                        students: [{ id: studentId, name: 'Auto Created Student' }],
                        teachers: [{ id: teacherId, name: 'Auto Created Teacher' }],
                    },
                ],
            } as CreateExamAssignmentDTO;

            const res = await apiRequest<IdResponseDTO>(
                'post',
                'assessments/examAssignments',
                createAssignmentParams
            );
            const assignmentId = res.data.id;

            try {
                const res = await apiRequest<IdResponseDTO>(
                    'GET',
                    `assessments/studentExamView/createdAttempt?studentId=${studentId}&examAssignmentId=${assignmentId}`
                );
                const attemptId = res.data.id;

                setCreatedAttemptId(attemptId);
                setAttemptId(attemptId);
            } catch {
                setError('Failed to get created attempt ID.');
            }
        } catch {
            setError('Failed to create exam attempt.');
        } finally {
            setLoading(false);
        }
    }, [selectedExamId, createdAttemptId, loading, examData]);

    return (
        <Container className="user-min-font-size">
            <h1 className="page-header">Create Exam Attempt</h1>
            <br />
            <div>If you know your attempt ID, you can enter it below to start the exam.</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                <SimpleTextField
                    label="Attempt ID"
                    name="attemptId"
                    onChange={(e) => setAttemptId(Number(e.target.value))}
                    value={attemptId.toString()}
                    sx={{ marginBottom: 0, minWidth: 180, maxWidth: 200 }}
                />
                <SimpleButton onClick={handleSetAttemptId} disabled={!attemptId}>
                    Load Attempt
                </SimpleButton>
            </div>

            <div className="mt-16" />

            <div>Set up a new attempt here.</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                <SimpleSingleSelect
                    label="Select Exam"
                    name="selectedExamId"
                    value={selectedExamId}
                    onChange={(e) => setSelectedExamId(Number(e.target.value))}
                    options={
                        filteredExamOptions.length
                            ? filteredExamOptions
                            : [getZeroOption('No Exams Found')]
                    }
                    sx={{ marginBottom: 0, minWidth: 220 }}
                />
                <SimpleTextField
                    label="Filter Exams"
                    name="examSearch"
                    onChange={(e) => setExamSearch(e.target.value)}
                    value={examSearch}
                    sx={{ marginBottom: 0, minWidth: 180, maxWidth: 200 }}
                />
                <SimpleButton
                    onClick={handleCreateAttempt}
                    disabled={!selectedExamId || loading || createdAttemptId > 0}
                >
                    Create
                </SimpleButton>
            </div>

            {createdAttemptId > 0 && (
                <div style={{ marginTop: 16 }}>
                    Successfully created exam attempt! Your attempt ID is <b>{createdAttemptId}</b>.
                    Click Load Attempt to begin.
                </div>
            )}

            {loading && <Loading size="lg" />}
            <ErrorMessage error={error} />
        </Container>
    );
};

export default CreateExamAttemptPage;
